import React, { useEffect, useState } from 'react';
import RialSymbol from '../components/RialSymbol';
import {
  Avatar, Box, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  IconButton, InputAdornment, InputLabel, MenuItem, Paper,
  Select, SelectChangeEvent, Snackbar, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography, Alert, Button,
  Divider,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Close, Delete as DeleteIcon, Email as EmailIcon, Phone as PhoneIcon,
  LocationOn as LocationIcon, Search, Groups as GroupsIcon,
} from '@mui/icons-material';
import axios from '../api';
import PaginationControls from '../components/PaginationControls';
import LoadingAnimation from '../components/LoadingAnimation';

interface BanquetHall {
  _id?: string;
  id?: string;
  name: string;
  description: string;
  images?: string[];
  location: { address: string; city: string };
  capacity: { min: number; max: number };
  pricePerEvent: number;
  amenities?: string[];
  contact: { phone: string; email?: string };
  status: 'pending' | 'verified' | 'rejected' | 'suspended';
  rejectionReason?: string | null;
  business?: { name?: string; location?: any; contact?: any };
}

export default function BanquetHalls() {
  const [halls, setHalls] = useState<BanquetHall[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedHall, setSelectedHall] = useState<BanquetHall | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [serverStats, setServerStats] = useState<{ total: number; verified: number; pending: number; rejected: number; suspended: number } | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, debouncedQuery]);

  useEffect(() => {
    fetchHalls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, statusFilter, debouncedQuery]);

  const fetchHalls = async () => {
    try {
      const response = await axios.get('/api/admin/banquet-halls', {
        params: {
          page,
          limit: rowsPerPage,
          status: statusFilter !== 'All' ? statusFilter : undefined,
          search: debouncedQuery || undefined,
        },
      });
      setHalls(response.data.banquetHalls || []);
      setTotal(response.data.total ?? response.data.banquetHalls?.length ?? 0);
      setServerStats(response.data.stats || null);
      setError(false);
    } catch (err) {
      console.error('Error fetching banquet halls:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // The backend already applied the status/search filters and pagination,
  // so this page's rows ARE the filtered results.
  const filteredHalls = halls;

  const stats = serverStats || {
    total: halls.length,
    verified: halls.filter(h => h.status === 'verified').length,
    pending: halls.filter(h => h.status === 'pending').length,
    rejected: halls.filter(h => h.status === 'rejected').length,
    suspended: halls.filter(h => h.status === 'suspended').length,
  };

  const handleVerify = async (id: string) => {
    try {
      await axios.put(`/api/admin/banquet-halls/${id}/verify`);
      fetchHalls();
      setSnackbar({ open: true, message: 'Banquet hall verified successfully', severity: 'success' });
      setDetailsOpen(false);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to verify banquet hall', severity: 'error' });
    }
  };

  const handleReject = async () => {
    if (!selectedHall) return;
    try {
      await axios.put(`/api/admin/banquet-halls/${selectedHall._id || selectedHall.id}/reject`, {
        reason: rejectReason,
      });
      fetchHalls();
      setSnackbar({ open: true, message: 'Banquet hall rejected', severity: 'success' });
      setRejectDialogOpen(false);
      setDetailsOpen(false);
      setRejectReason('');
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to reject banquet hall', severity: 'error' });
    }
  };

  const handleSuspend = async (id: string) => {
    try {
      await axios.put(`/api/admin/banquet-halls/${id}/suspend`);
      fetchHalls();
      setSnackbar({ open: true, message: 'Banquet hall suspended and removed from the marketplace', severity: 'success' });
      setDetailsOpen(false);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to suspend banquet hall', severity: 'error' });
    }
  };

  const handleRemove = async () => {
    const id = selectedHall?._id || selectedHall?.id;
    if (!id) return;
    try {
      await axios.delete(`/api/admin/banquet-halls/${id}`);
      await fetchHalls();
      setSnackbar({ open: true, message: 'Banquet hall removed from the marketplace', severity: 'success' });
      setRemoveDialogOpen(false);
      setDetailsOpen(false);
      setSelectedHall(null);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to remove banquet hall', severity: 'error' });
    }
  };

  const handleViewDetails = (hall: BanquetHall) => {
    setSelectedHall(hall);
    setDetailsOpen(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Banquet Halls</Typography>
          <Typography color="text.secondary">Review and verify merchant-listed banquet halls.</Typography>
        </Box>
        <Button variant="contained" color={stats.pending > 0 ? 'warning' : 'inherit'}>
          Pending ({stats.pending})
        </Button>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Total Halls</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>{stats.total}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Verified</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>{stats.verified}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Pending</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>{stats.pending}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Suspended</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>{stats.suspended}</Typography>
        </Paper>
      </Box>

      <Card>
        <Box sx={{ display: 'flex', gap: 2, p: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Search halls"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 300 } }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}>
              {['All', 'pending', 'verified', 'rejected', 'suspended'].map((opt) => (
                <MenuItem key={opt} value={opt === 'All' ? 'All' : opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {error && <Alert severity="error" sx={{ m: 2 }}>Unable to load banquet halls. Check that the backend is running.</Alert>}

        {!error && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default' }}>
                  <TableCell><strong>Photo</strong></TableCell>
                  <TableCell><strong>Hall Name</strong></TableCell>
                  <TableCell><strong>Business</strong></TableCell>
                  <TableCell><strong>City</strong></TableCell>
                  <TableCell><strong>Capacity</strong></TableCell>
                  <TableCell><strong>Price/Event</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <LoadingAnimation message="Loading banquet halls..." subtitle="Fetching listings" />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredHalls.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">No banquet halls found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredHalls.map((hall) => {
                  const hallId = hall._id || hall.id;
                  return (
                    <TableRow key={hallId} hover>
                      <TableCell>
                        <Avatar src={hall.images?.[0]} alt={hall.name} variant="rounded" sx={{ width: 40, height: 40 }}>
                          {hall.name?.[0]}
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 600 }}>{hall.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{hall.description?.substring(0, 50)}</Typography>
                      </TableCell>
                      <TableCell>{hall.business?.name || 'Unknown'}</TableCell>
                      <TableCell>{hall.location?.city || 'N/A'}</TableCell>
                      <TableCell>{hall.capacity?.min}-{hall.capacity?.max} guests</TableCell>
                      <TableCell><RialSymbol /> {hall.pricePerEvent?.toFixed(2)}</TableCell>
                      <TableCell>
                        <Chip
                          label={hall.status}
                          color={
                            hall.status === 'verified' ? 'success' :
                              hall.status === 'pending' ? 'warning' :
                                hall.status === 'rejected' ? 'error' : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="primary" onClick={() => handleViewDetails(hall)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <PaginationControls
        page={page}
        total={total}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => { setRowsPerPage(value); setPage(1); }}
      />

      {/* Hall Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Banquet Hall Details
          <IconButton onClick={() => setDetailsOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedHall && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Hall Name</Typography>
                <Typography sx={{ fontWeight: 600 }}>{selectedHall.name}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Listed By</Typography>
                <Typography>{selectedHall.business?.name || 'Unknown business'}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                <Typography>{selectedHall.description}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Capacity</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupsIcon fontSize="small" />
                  <Typography>{selectedHall.capacity?.min} - {selectedHall.capacity?.max} guests</Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Price per Event</Typography>
                <Typography sx={{ fontWeight: 600 }}><RialSymbol /> {selectedHall.pricePerEvent?.toFixed(2)}</Typography>
              </Box>

              {!!selectedHall.amenities?.length && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Amenities</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                    {selectedHall.amenities.map((a) => (
                      <Chip key={a} label={a} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </Box>
              )}

              <Divider />

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Contact Information</Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {!!selectedHall.contact?.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon fontSize="small" />
                      <Typography>{selectedHall.contact.email}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="small" />
                    <Typography>{selectedHall.contact?.phone}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon fontSize="small" />
                    <Typography>{selectedHall.location?.address}, {selectedHall.location?.city}</Typography>
                  </Box>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip
                  label={selectedHall.status}
                  color={
                    selectedHall.status === 'verified' ? 'success' :
                      selectedHall.status === 'pending' ? 'warning' :
                        selectedHall.status === 'rejected' ? 'error' : 'default'
                  }
                  size="small"
                  sx={{ mt: 1 }}
                />
                {selectedHall.status === 'rejected' && !!selectedHall.rejectionReason && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Reason: {selectedHall.rejectionReason}
                  </Typography>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setRemoveDialogOpen(true)}
          >
            Remove
          </Button>
          {selectedHall?.status === 'pending' && (
            <>
              <Button color="error" onClick={() => setRejectDialogOpen(true)}>
                Reject
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleVerify(selectedHall._id || selectedHall.id || '')}
              >
                Verify
              </Button>
            </>
          )}
          {selectedHall?.status === 'verified' && (
            <Button color="error" onClick={() => handleSuspend(selectedHall._id || selectedHall.id || '')}>
              Suspend
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Removing is intentionally a soft delete so past bookings stay valid. */}
      <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove banquet hall?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            {selectedHall?.name} will be removed from the marketplace. Existing bookings will be kept for reporting.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={handleRemove}>
            Remove hall
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Banquet Hall</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Explain why this banquet hall is being rejected..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={!rejectReason}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
